import React, { useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { ShieldCheck, Users, Home as HomeIcon, MapPin, AlertTriangle, BadgePercent, Check, ChevronRight, RefreshCw, PhoneCall } from "lucide-react";
import Header from "../components/Header";
import Footer from "../components/Footer";
import Breadcrumbs from "../components/Breadcrumbs";
import { Toaster } from "../components/ui/toaster";
import { ValuationProvider, useAppModals } from "../components/ValuationDialog";
import { Accordion, AccordionContent, AccordionItem, AccordionTrigger } from "../components/ui/accordion";
import Seo from "../components/Seo";
import { CONTACT } from "../mock";

const UPDATED = "February 2025";

const HMO_CRUMBS = [{ label: "Home", to: "/" }, { label: "Landlords", to: "/landlords" }, { label: "HMO & Licensing Guide" }];

const SCHEMES = [
  { icon: Users, title: "Mandatory HMO Licensing", who: "Larger HMOs let to 5 or more people forming 2+ households who share a kitchen, bathroom or toilet.", note: "Applies England-wide, regardless of the number of storeys." },
  { icon: HomeIcon, title: "Additional HMO Licensing", who: "Smaller HMOs \u2014 typically 3\u20134 people in 2+ households sharing facilities \u2014 under Hackney\u2019s borough-wide scheme.", note: "Covers house shares that fall below the mandatory threshold." },
  { icon: MapPin, title: "Selective Licensing", who: "Most privately rented homes (including single households) let within designated wards.", note: "Applies to specific wards \u2014 always confirm your ward is included." },
];

const WARDS = ["Brownswood", "Cazenove", "Stoke Newington", "Hackney Central", "Hackney Downs", "Dalston", "Clissold", "Hoxton East & Shoreditch", "Hoxton West", "Springfield", "Woodberry Down", "De Beauvoir"];

const FAQ = [
  { q: "Do I need an HMO licence in Hackney?", a: "If your property is let to five or more people from two or more households who share facilities, you need a Mandatory HMO licence. Smaller shared houses (usually 3\u20134 sharers) generally need an Additional HMO licence under Hackney\u2019s borough-wide scheme." },
  { q: "What is selective licensing and does it affect my property?", a: "Selective licensing requires a licence for most privately rented homes \u2014 including single family lets \u2014 in designated wards. Whether it applies depends on the property\u2019s ward, so it\u2019s essential to check the current ward list with Hackney Council or ask us to check for you." },
  { q: "How much does a Hackney licence cost?", a: "Fees vary by scheme and are usually split into an application (Part A) and a grant (Part B) stage. Hackney offers discounts \u2014 for example for properties with an EPC rating of A\u2013C and for members of recognised landlord accreditation schemes. Fees change periodically, so confirm the current figures on the council website before applying." },
  { q: "What are the penalties for letting without a licence?", a: "Letting a licensable property without a licence is a criminal offence. Councils can issue civil penalties of up to \u00a330,000 per offence or prosecute for an unlimited fine, and tenants or the council can apply for a Rent Repayment Order reclaiming up to 12 months\u2019 rent. You may also be unable to serve a valid Section 21 notice." },
  { q: "How long does a licence last?", a: "Licences are typically granted for up to five years, though shorter terms can be issued. You must apply to renew before it expires to stay compliant." },
];

function Checker() {
  const [step, setStep] = useState(0);
  const [result, setResult] = useState(null);
  const navigate = useNavigate();
  const answer = (r) => { setResult(r); setStep(3); };
  const reset = () => { setStep(0); setResult(null); };

  const RESULTS = {
    mandatory: { title: "Likely: Mandatory HMO Licence", text: "A home let to 5+ people in 2+ households sharing facilities almost always needs a Mandatory HMO licence." },
    additional: { title: "Likely: Additional HMO Licence", text: "A smaller shared house (usually 3\u20134 sharers in 2+ households) typically needs an Additional HMO licence under Hackney\u2019s borough-wide scheme." },
    selective: { title: "Possibly: Selective Licence", text: "Single-household lets may need a Selective licence if the property sits in a designated ward. This must be checked against the current ward list." },
  };

  return (
    <div className="rounded-2xl border border-[#e5ddd0] bg-white p-7 shadow-sm">
      <div className="flex items-center gap-2">
        <ShieldCheck size={20} className="text-[#35a4a4]" />
        <h3 className="font-display text-xl font-600 uppercase tracking-wide text-[#16323f]">Do I need a licence?</h3>
      </div>
      <p className="mt-2 text-sm text-[#7a716a]">Answer a couple of quick questions for a rough indication.</p>

      {step === 0 && (
        <div className="mt-5">
          <p className="font-medium text-[#16323f]">Is the property let to 5 or more people from 2+ households who share a kitchen or bathroom?</p>
          <div className="mt-4 flex gap-3">
            <button onClick={() => answer("mandatory")} className="rounded-full bg-[#16323f] px-6 py-2.5 text-xs font-semibold uppercase tracking-widest text-white hover:bg-[#ec008c]">Yes</button>
            <button onClick={() => setStep(1)} className="rounded-full border border-[#16323f]/25 px-6 py-2.5 text-xs font-semibold uppercase tracking-widest text-[#16323f] hover:border-[#ec008c] hover:text-[#ec008c]">No</button>
          </div>
        </div>
      )}
      {step === 1 && (
        <div className="mt-5">
          <p className="font-medium text-[#16323f]">Is it shared by 3 or 4 people forming 2+ households (a house share)?</p>
          <div className="mt-4 flex gap-3">
            <button onClick={() => answer("additional")} className="rounded-full bg-[#16323f] px-6 py-2.5 text-xs font-semibold uppercase tracking-widest text-white hover:bg-[#ec008c]">Yes</button>
            <button onClick={() => setStep(2)} className="rounded-full border border-[#16323f]/25 px-6 py-2.5 text-xs font-semibold uppercase tracking-widest text-[#16323f] hover:border-[#ec008c] hover:text-[#ec008c]">No</button>
          </div>
        </div>
      )}
      {step === 2 && (
        <div className="mt-5">
          <p className="font-medium text-[#16323f]">Is it a single household (e.g. a family or one couple) renting in Hackney?</p>
          <div className="mt-4 flex gap-3">
            <button onClick={() => answer("selective")} className="rounded-full bg-[#16323f] px-6 py-2.5 text-xs font-semibold uppercase tracking-widest text-white hover:bg-[#ec008c]">Yes</button>
            <button onClick={() => answer("selective")} className="rounded-full border border-[#16323f]/25 px-6 py-2.5 text-xs font-semibold uppercase tracking-widest text-[#16323f] hover:border-[#ec008c] hover:text-[#ec008c]">Not sure</button>
          </div>
        </div>
      )}
      {step === 3 && result && (
        <div className="mt-5">
          <div className="rounded-xl bg-[#f3ede3] p-5">
            <p className="font-display text-lg font-600 text-[#16323f]">{RESULTS[result].title}</p>
            <p className="mt-2 text-sm leading-relaxed text-[#5b5450]">{RESULTS[result].text}</p>
            <p className="mt-3 text-xs text-[#7a716a]">This is a general indication only, not legal advice. Ward boundaries and rules change — we’ll confirm exactly what applies to your property.</p>
          </div>
          <div className="mt-4 flex flex-wrap gap-3">
            <button onClick={() => navigate("/landlords")} className="flex items-center gap-2 rounded-full bg-[#ec008c] px-6 py-2.5 text-xs font-semibold uppercase tracking-widest text-white hover:bg-[#c60074]">
              Talk to us <ChevronRight size={15} />
            </button>
            <button onClick={reset} className="flex items-center gap-2 rounded-full border border-[#16323f]/25 px-6 py-2.5 text-xs font-semibold uppercase tracking-widest text-[#16323f] hover:border-[#ec008c] hover:text-[#ec008c]">
              <RefreshCw size={14} /> Start again
            </button>
          </div>
        </div>
      )}
    </div>
  );
}

function Content() {
  const { openValuation } = useAppModals();

  React.useEffect(() => {
    const id = "licensing-faq-jsonld";
    const data = { "@context": "https://schema.org", "@type": "FAQPage",
      mainEntity: FAQ.map((f) => ({ "@type": "Question", name: f.q, acceptedAnswer: { "@type": "Answer", text: f.a } })) };
    let s = document.getElementById(id);
    if (!s) { s = document.createElement("script"); s.id = id; s.type = "application/ld+json"; document.head.appendChild(s); }
    s.textContent = JSON.stringify(data);
    return () => { const el = document.getElementById(id); if (el) el.remove(); };
  }, []);

  return (
    <div className="App">
      <Seo title="Hackney HMO & Selective Licensing: What Landlords Need to Know | New Space"
        description="A landlord's guide to Hackney's HMO and selective licensing schemes \u2014 who's affected, the wards covered, fees, EPC and accreditation discounts, and the penalties for letting unlicensed."
        path="/landlords/hmo-licensing-hackney" />
      <Header />

      {/* Hero */}
      <section className="relative overflow-hidden bg-[#16323f] pt-32 pb-16 lg:pt-44">
        <div className="mx-auto max-w-[900px] px-5 text-center">
          <div className="mb-6 flex justify-center">
            <Breadcrumbs
              variant="light"
              items={HMO_CRUMBS}
            />
          </div>
          <p className="text-xs font-semibold uppercase tracking-[0.3em] text-[#d8b074]">Landlord Guide</p>
          <h1 className="mt-4 font-display text-3xl font-700 uppercase leading-tight tracking-wide text-white lg:text-5xl">
            Hackney HMO &amp; Selective Licensing: What Landlords Need to Know
          </h1>
          <p className="mt-4 text-sm text-white/70">Last updated: {UPDATED}</p>
        </div>
      </section>

      <section className="bg-[#faf7f0] py-14 lg:py-20">
        <div className="mx-auto max-w-[900px] space-y-12 px-5">
          <p className="text-[15px] leading-relaxed text-[#5b5450]">
            If you let a property in Hackney, there’s a good chance it needs a licence. The borough runs some of
            the most extensive licensing schemes in London, and letting without the right licence can be costly.
            Here’s a plain-English overview of the three schemes, who they affect and what to do next.
          </p>

          {/* Three schemes */}
          <div>
            <h2 className="font-display text-2xl font-600 uppercase tracking-wide text-[#16323f]">The three schemes</h2>
            <span className="mt-3 block h-[3px] w-14 rounded-full bg-[#eaa32a]" />
            <div className="mt-6 grid gap-5 md:grid-cols-3">
              {SCHEMES.map((s) => (
                <div key={s.title} className="rounded-2xl border border-[#e5ddd0] bg-white p-6">
                  <span className="grid h-12 w-12 place-items-center rounded-full bg-[#16323f] text-[#d8b074]"><s.icon size={22} /></span>
                  <h3 className="mt-4 font-display text-lg font-600 text-[#16323f]">{s.title}</h3>
                  <p className="mt-2 text-sm leading-relaxed text-[#5b5450]">{s.who}</p>
                  <p className="mt-2 text-xs text-[#7a716a]">{s.note}</p>
                </div>
              ))}
            </div>
          </div>

          {/* Checker */}
          <Checker />

          {/* Wards */}
          <div>
            <h2 className="font-display text-2xl font-600 uppercase tracking-wide text-[#16323f]">Wards in the selective licensing zone</h2>
            <span className="mt-3 block h-[3px] w-14 rounded-full bg-[#eaa32a]" />
            <p className="mt-4 text-[15px] leading-relaxed text-[#5b5450]">
              Selective licensing applies to privately rented homes in designated wards, which have included the
              following. Boundaries are reviewed periodically, so always confirm your ward is currently covered:
            </p>
            <div className="mt-5 flex flex-wrap gap-2">
              {WARDS.map((w) => (
                <span key={w} className="rounded-full border border-[#e5ddd0] bg-white px-4 py-1.5 text-sm text-[#16323f]">{w}</span>
              ))}
            </div>
          </div>

          {/* Fees & discounts */}
          <div>
            <h2 className="font-display text-2xl font-600 uppercase tracking-wide text-[#16323f]">Fees &amp; discounts</h2>
            <span className="mt-3 block h-[3px] w-14 rounded-full bg-[#eaa32a]" />
            <p className="mt-4 text-[15px] leading-relaxed text-[#5b5450]">
              Licence fees are usually split into two parts — an application fee (Part A) and a grant fee (Part B).
              Hackney offers discounts that can meaningfully reduce the cost:
            </p>
            <ul className="mt-4 space-y-3">
              {[
                "EPC A\u2013C discount \u2014 a reduced fee for energy-efficient properties rated A to C.",
                "Accredited landlord discount \u2014 for members of recognised accreditation schemes (e.g. the London Landlord Accreditation Scheme).",
                "Early-application savings \u2014 applying before a deadline or renewing on time can avoid higher fees.",
              ].map((t) => (
                <li key={t} className="flex items-start gap-3 text-[15px] text-[#5b5450]">
                  <span className="mt-0.5 grid h-6 w-6 shrink-0 place-items-center rounded-full bg-[#35a4a4] text-white"><BadgePercent size={13} /></span>{t}
                </li>
              ))}
            </ul>
            <p className="mt-4 text-xs text-[#7a716a]">Exact fee amounts change periodically — confirm the current figures on Hackney Council’s website before you apply.</p>
          </div>

          {/* Penalties */}
          <div className="rounded-2xl border border-[#eaa32a]/40 bg-[#fff8ec] p-6">
            <div className="flex items-center gap-2">
              <AlertTriangle size={20} className="text-[#b9822a]" />
              <h2 className="font-display text-xl font-600 uppercase tracking-wide text-[#16323f]">Penalties for letting unlicensed</h2>
            </div>
            <ul className="mt-4 space-y-2 text-[15px] leading-relaxed text-[#5b5450]">
              <li className="flex gap-2"><Check size={18} className="mt-0.5 shrink-0 text-[#b9822a]" /> Civil penalties of up to £30,000 per offence, or prosecution and an unlimited fine.</li>
              <li className="flex gap-2"><Check size={18} className="mt-0.5 shrink-0 text-[#b9822a]" /> Rent Repayment Orders reclaiming up to 12 months’ rent.</li>
              <li className="flex gap-2"><Check size={18} className="mt-0.5 shrink-0 text-[#b9822a]" /> Inability to serve a valid Section 21 (no-fault) eviction notice.</li>
              <li className="flex gap-2"><Check size={18} className="mt-0.5 shrink-0 text-[#b9822a]" /> Risk of a banning order for repeat or serious breaches.</li>
            </ul>
          </div>

          {/* FAQ */}
          <div>
            <h2 className="font-display text-2xl font-600 uppercase tracking-wide text-[#16323f]">Frequently asked questions</h2>
            <span className="mt-3 block h-[3px] w-14 rounded-full bg-[#eaa32a]" />
            <Accordion type="single" collapsible className="mt-6 space-y-3">
              {FAQ.map((f, i) => (
                <AccordionItem key={i} value={`lf-${i}`} className="overflow-hidden rounded-2xl border border-[#e5ddd0] bg-white px-5">
                  <AccordionTrigger className="py-5 text-left font-display text-lg font-500 text-[#16323f] hover:no-underline">{f.q}</AccordionTrigger>
                  <AccordionContent className="pb-5 text-[15px] leading-relaxed text-[#5b5450]">{f.a}</AccordionContent>
                </AccordionItem>
              ))}
            </Accordion>
          </div>

          <p className="rounded-xl bg-[#f3ede3] p-4 text-xs leading-relaxed text-[#7a716a]">
            This guide is for general information only and does not constitute legal advice. Licensing schemes, ward
            boundaries and fees change over time — last reviewed {UPDATED}. Always confirm the current position with
            the London Borough of Hackney, or ask New Space to check for you.
          </p>
        </div>
      </section>

      {/* CTA */}
      <section className="bg-[#16323f] py-16">
        <div className="mx-auto flex max-w-[900px] flex-col items-center px-5 text-center">
          <h2 className="font-display text-3xl font-600 uppercase tracking-wide text-white lg:text-4xl">
            Not sure if your property needs a licence?
          </h2>
          <p className="mt-3 text-white/75">Our team knows Hackney’s schemes inside out. We’ll check your property and help you stay compliant.</p>
          <div className="mt-8 flex flex-col gap-4 sm:flex-row">
            <button onClick={openValuation} className="group flex items-center justify-center gap-2 rounded-full bg-[#ec008c] px-9 py-3.5 text-xs font-semibold uppercase tracking-[0.2em] text-white transition-colors hover:bg-[#c60074]">
              Talk to us <ChevronRight size={16} className="transition-transform group-hover:translate-x-1" />
            </button>
            <a href={`tel:${CONTACT.phone.replace(/\s/g, "")}`} className="flex items-center justify-center gap-2 rounded-full border border-white/60 px-9 py-3.5 text-xs font-semibold uppercase tracking-[0.2em] text-white transition-colors hover:bg-white hover:text-[#16323f]">
              <PhoneCall size={15} /> {CONTACT.phone}
            </a>
          </div>
          <Link to="/landlords" className="mt-6 text-sm text-white/70 underline hover:text-white">Back to landlord services</Link>
        </div>
      </section>

      <Footer />
      <Toaster />
    </div>
  );
}

export default function LicensingGuide() {
  return (
    <ValuationProvider>
      <Content />
    </ValuationProvider>
  );
}
